'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { getPackageModuleByName, updatePackageMenuAll } from '../../../stores/roleStore';
import { useLanguage } from '../../../utils/i18n';

const PackageTreeNode = ({ node, level = 0, isEditAll, isSearching, onFeatureChange }) => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (isEditAll || isSearching) setOpen(true);
  }, [isEditAll, isSearching]);

  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const handleToggle = () => {
    if (hasChildren) setOpen(!open);
  };

  const getFeatureStatus = (featName) => {
    if (!node.features) return false;
    const f = node.features.find((x) => String(x.name).toLowerCase() === featName.toLowerCase());
    return f ? f.status : false;
  };

  const handleCheckChange = (featName, checked) => {
    if (!isEditAll) return;
    onFeatureChange(node.id, featName, checked);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          px: 2,
          borderBottom: '1px solid #f0f0f0',
          bgcolor: open && level === 0 ? '#f8f9fa' : 'transparent',
          '&:hover': { bgcolor: '#f4f6f8' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, pl: level * 4 }}>
          {hasChildren ? (
            <IconButton size="small" onClick={handleToggle} sx={{ mr: 1, color: 'text.secondary' }}>
              {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          ) : (
            <Box sx={{ width: 34, mr: 1 }} />
          )}
          <Typography variant="body1" fontWeight={hasChildren ? 600 : 400}>
            {node.alias || node.module_name || node.modules_name}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {['read', 'create', 'update', 'delete', 'report'].map((f) => {
            // Mapping nama fitur dari update ke edit agar sesuai dengan struktur DB
            const featNameDB = f === 'update' ? 'edit' : f;
            const getFeatureColor = (feat) => {
              switch (feat) {
                case 'read':
                  return '#2196f3';
                case 'create':
                  return '#4caf50';
                case 'update':
                case 'edit':
                  return '#ff9800';
                case 'delete':
                  return '#f44336';
                case 'report':
                  return '#9c27b0';
                default:
                  return 'primary.main';
              }
            };
            return (
              <FormControlLabel
                key={f}
                control={
                  <Checkbox
                    size="small"
                    checked={getFeatureStatus(featNameDB)}
                    onChange={(e) => handleCheckChange(featNameDB, e.target.checked)}
                    disableRipple={!isEditAll}
                    sx={{
                      cursor: isEditAll ? 'pointer' : 'default',
                      color: getFeatureColor(f),
                      '&.Mui-checked': { color: getFeatureColor(f) }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {f}
                  </Typography>
                }
                sx={{ m: 0, pointerEvents: isEditAll ? 'auto' : 'none' }}
              />
            );
          })}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={node.is_active}
                onChange={(e) => handleCheckChange('active', e.target.checked)}
                disableRipple={!isEditAll}
                sx={{
                  cursor: isEditAll ? 'pointer' : 'default',
                  color: '#607d8b',
                  '&.Mui-checked': { color: '#607d8b' }
                }}
              />
            }
            label={<Typography variant="body2">{t('packageMenu.active', 'Active')}</Typography>}
            sx={{ m: 0, pointerEvents: isEditAll ? 'auto' : 'none' }}
          />
        </Box>
      </Box>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {node.children.map((childNode) => (
              <PackageTreeNode
                key={childNode.id || childNode.module_name}
                node={childNode}
                level={level + 1}
                isEditAll={isEditAll}
                isSearching={isSearching}
                onFeatureChange={onFeatureChange}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const ListPackageMenuDialog = ({ open, onClose, packageData }) => {
  const notify = useNotifier();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const isEditAll = true;
  const [isSavingAll, setIsSavingAll] = useState(false);

  const getFilteredTree = (nodes, query) => {
    if (!query.trim()) return nodes;
    const lowerQuery = query.toLowerCase();

    return nodes.reduce((acc, node) => {
      const filteredChildren = node.children ? getFilteredTree(node.children, query) : [];
      const isMatch = (node.alias || node.module_name || node.modules_name || '').toLowerCase().includes(lowerQuery);

      if (isMatch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }
      return acc;
    }, []);
  };

  const displayedMenuItems = getFilteredTree(menuItems, searchQuery);
  const isSearching = !!searchQuery.trim();

  useEffect(() => {
    const fetchMenuData = async () => {
      if (!packageData?.package_name) return;

      setLoading(true);
      try {
        const res = await getPackageModuleByName(packageData.package_name);
        let rootMenuData = res?.data_modules || res?.data || res;
        let items = Array.isArray(rootMenuData) ? rootMenuData : rootMenuData ? [rootMenuData] : [];

        setMenuItems(JSON.parse(JSON.stringify(items)));
      } catch (error) {
        console.error(error);
        notify({ message: t('packageMenu.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    if (open) fetchMenuData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packageData]);

  const updateNodeInTree = (nodes, id, featName, newStatus) => {
    return nodes.map((node) => {
      if (node.id === id) {
        if (featName === 'active') {
          node.is_active = newStatus;
        } else {
          const featIndex = node.features.findIndex((f) => String(f.name).toLowerCase() === featName.toLowerCase());
          if (featIndex > -1) {
            node.features[featIndex].status = newStatus;
          }
        }
        return { ...node };
      }

      if (node.children && node.children.length > 0) {
        node.children = updateNodeInTree(node.children, id, featName, newStatus);
      }
      return node;
    });
  };

  const handleFeatureChange = useCallback((id, featName, checked) => {
    setMenuItems((prev) => updateNodeInTree(prev, id, featName, checked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setIsSavingAll(true);
    try {
      const payload = {
        id: packageData.id,
        name: packageData.package_name,
        data_modules: menuItems
      };

      await updatePackageMenuAll(payload);

      notify({
        message: t('packageMenu.packageMenuUpdatedSuccess', 'The package menu has been updated successfully!'),
        severity: 'success'
      });
      if (onClose) onClose();
    } catch (e) {
      console.error(e);
      notify({ message: t('packageMenu.failedSaveAccessChanges', 'Failed to save access changes'), severity: 'error' });
    } finally {
      setIsSavingAll(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={`${t('packageMenu.setupAccess', 'Setup Access:')} ${packageData?.package_name || '-'}`}
      maxWidth="lg"
      fullScreenMode={true}
      actions={
        <Button onClick={onClose} variant="outlined" sx={{ color: 'text.secondary', borderColor: 'text.secondary' }}>
          {t('general.close', 'Close')}
        </Button>
      }
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {t('packageMenu.packageFeaturesConfig', 'Package Features Config')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder={t('packageMenu.searchMenu', 'Search menu...')}
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 250, bgcolor: '#fff', borderRadius: 1, '& .MuiInputBase-root': { height: 40 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
            <Button
              variant="contained"
              color="info"
              startIcon={isSavingAll ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveAll}
              disabled={isSavingAll}
              disableElevation
              sx={{ height: 40, textTransform: 'none' }}
            >
              {isSavingAll ? t('general.saving', 'Saving...') : t('packageMenu.saveAllChanges', 'Save All Changes')}
            </Button>
          </Box>
        </Box>
        <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0', overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
            </Box>
          ) : displayedMenuItems.length > 0 ? (
            displayedMenuItems.map((item) => (
              <PackageTreeNode
                key={item.id || item.module_name}
                node={item}
                isEditAll={isEditAll}
                isSearching={isSearching}
                onFeatureChange={handleFeatureChange}
              />
            ))
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>
                {searchQuery
                  ? t('packageMenu.noMenuMatch', 'No matching menu found.')
                  : t('packageMenu.noMenuData', 'No menu module data for this package.')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </FormDialog>
  );
};

export default ListPackageMenuDialog;
